/**
 * Permanent usage rollups, eagerly folded and rebuildable from the cold layer
 * (gateway 0126, router#3190).
 *
 * Every aggregate that outlives `usage_logs` / `usage_rated_events` (the org ×
 * hour rollup, the router-client identity rollup, the router-stats and the
 * account daily rollups, and the day × dimension rollup added here) must hold
 * its day BEFORE that day leaves the hot window, with late ratings folded in,
 * and must be rebuildable from the cold layer afterwards. Three families share
 * one state table (`usage_rollup_days`):
 *
 *   - `dim`      `usage_dim_daily_rollup`, from which `router_stats_daily_rollup`
 *                and `usage_daily_rollup` are derived in the same transaction;
 *   - `hourly`   `usage_org_hourly` (+ `_hours`), whose hot folder stays the
 *                org-hourly fold; the cold path only verifies / repairs;
 *   - `identity` `router_client_identity_daily` (+ `_days`), same.
 *
 * Two writers, one rule each:
 *
 *   - `POST /internal/usage-rollups/fold` (gateway, driven by control-loops
 *     `usage_rollup_fold`): folds complete Shanghai days of the `dim` family
 *     from the hot `usage_logs` while the day is still hot, refolds days with
 *     pending verdicts or a changed fingerprint, and reports ALERTS.
 *   - the platform's `POST /internal/usage-rollups/cold-refold` (control-loops
 *     `usage_rollup_cold_refold`): for days that left the hot window, reads
 *     `usage.logs` / `usage.rated_events` through the cold reader, aggregates
 *     with the very same rules, and hands the result to the gateway
 *     (`cold/stage` + `cold/commit`), which compares it with what is stored,
 *     replaces what differs and records the correction. The gateway never
 *     reads the cold layer; the platform never writes the rollup tables.
 */

export const USAGE_ROLLUP_MOUNT = '/internal/usage-rollups';
export const USAGE_ROLLUP_FOLD_PATH = `${USAGE_ROLLUP_MOUNT}/fold`;
export const USAGE_ROLLUP_COLD_PLAN_PATH = `${USAGE_ROLLUP_MOUNT}/cold/plan`;
export const USAGE_ROLLUP_COLD_STAGE_PATH = `${USAGE_ROLLUP_MOUNT}/cold/stage`;
export const USAGE_ROLLUP_COLD_COMMIT_PATH = `${USAGE_ROLLUP_MOUNT}/cold/commit`;
export const USAGE_ROLLUP_COLD_REPORT_PATH = `${USAGE_ROLLUP_MOUNT}/cold/report`;
export const USAGE_ROLLUP_COLD_CHECKPOINT_PATH = `${USAGE_ROLLUP_MOUNT}/cold/checkpoint`;
export const USAGE_ROLLUP_STATUS_PATH = `${USAGE_ROLLUP_MOUNT}/status`;
/** Platform route driven by control-loops `usage_rollup_cold_refold`. */
export const USAGE_ROLLUP_COLD_REFOLD_PATH = '/internal/usage-rollups/cold-refold';

export type UsageRollupFamily = 'dim' | 'hourly' | 'identity';
export const USAGE_ROLLUP_FAMILIES: readonly UsageRollupFamily[] = ['dim', 'hourly', 'identity'];

/** A day is folded once it ended at least this long ago (the usage rollup's own rating lag). */
export const USAGE_ROLLUP_SEAL_LAG_HOURS = 2;
/** A hot-folded day of the recent window is re-checked (fingerprint, pending verdicts) at most this often. */
export const USAGE_ROLLUP_RECHECK_INTERVAL_HOURS = 3;
/**
 * A hot-folded day OLDER than the recent window is re-checked at most this
 * often. Every day that is still hot is re-checked (late CN rows and late
 * verdicts arrive for any of them); the older ones just less eagerly.
 */
export const USAGE_ROLLUP_RECHECK_OLD_INTERVAL_HOURS = 12;
/** The recent window: days this new (or newer) are folded before any backfill and re-checked at the short interval. */
export const USAGE_ROLLUP_HOT_LOOKBACK_DAYS = 8;
/** Days this new are "new work": they go ahead of the history backfill in the queue. */
export const USAGE_ROLLUP_RECENT_DAYS = 3;
/** Days folded from hot per call, refolds included. */
export const USAGE_ROLLUP_MAX_DAYS_PER_FOLD = 2;
/** History (backfill) days folded per call, at most. The backfill is paced, not a burst. */
export const USAGE_ROLLUP_BACKFILL_DAYS_PER_FOLD = 1;
/**
 * A day whose usage rows are estimated at this many or more is folded in hour
 * slices from the start; a smaller day tries one scan first and falls back to
 * slices when that scan times out (the day is then marked, so it is not tried
 * whole again).
 */
export const USAGE_ROLLUP_SLICE_ROWS_THRESHOLD = 3_000_000;
/** A sliced day is cut on a grid of 15-minute cells (96 a day). */
export const USAGE_ROLLUP_SLICE_CELL_MINUTES = 15;
export const USAGE_ROLLUP_SLICE_CELLS_PER_DAY = 96;
/**
 * Cells per slice to begin with (one hour). A slice that uses its whole share
 * and times out halves the day's width (down to one cell) and runs again; a
 * slice that times out at one cell is a recorded failure.
 */
export const USAGE_ROLLUP_SLICE_CELLS_DEFAULT = 4;
/** The re-check phase of a call may use at most this much of its budget (new days go first). */
export const USAGE_ROLLUP_RECHECK_PHASE_MS = 30_000;
/** One whole-day scan may use at most this much of a call (the rest is left for the days behind it). */
export const USAGE_ROLLUP_DAY_ATTEMPT_MS = 120_000;
/** One slice may use at most this much. */
export const USAGE_ROLLUP_SLICE_ATTEMPT_MS = 90_000;
/** A failed (or timed-out) day is not tried again for this long. */
export const USAGE_ROLLUP_RETRY_AFTER_FAILURE_MS = 30 * 60_000;
/** Days re-checked per call. */
export const USAGE_ROLLUP_MAX_RECHECKS_PER_FOLD = 4;
/** Backfill depth of the cold path: the readers' widest window (90 days) plus a day. */
export const USAGE_ROLLUP_COLD_BACKFILL_DAYS = 91;
/** Identity rollup retention (gateway 0109 keeps today − 10). */
export const USAGE_ROLLUP_IDENTITY_RETENTION_DAYS = 10;
/** Cold refold depth of the org × hour family (the layer itself keeps `USAGE_ORG_HOURLY_RETENTION_DAYS`, 402; its backfill starts at 85). */
export const USAGE_ROLLUP_HOURLY_RETENTION_DAYS = 85;
/**
 * Wall clock one fold call may spend, and the statement bound of one day's
 * grouped scan: a whole day of `usage_logs` with its rated join takes tens of
 * seconds at today's volume, so the budget is minutes, not the seconds of the
 * hour-sized folds. Every 15 minutes; the day cap bounds the work per call.
 */
export const USAGE_ROLLUP_FOLD_BUDGET_MS = 180_000;
export const USAGE_ROLLUP_FOLD_MIN_BUDGET_MS = 3_000;
/** The cold path's wall clock per cron call. */
export const USAGE_ROLLUP_COLD_REFOLD_BUDGET_MS = 240_000;
export const USAGE_ROLLUP_COLD_REFOLD_MIN_BUDGET_MS = 20_000;
/** The lease `cold/plan` puts on the days it hands out. A crashed caller frees them after this. */
export const USAGE_ROLLUP_COLD_LEASE_MS = 20 * 60_000;
/** A cold-verified day is revision-checked this often (hours). */
export const USAGE_ROLLUP_COLD_REVISION_CHECK_HOURS = 6;
/** Revision checks handed out per plan call. */
export const USAGE_ROLLUP_COLD_REVISION_CHECKS_PER_PLAN = 6;
/**
 * A long cold scan parks its state on the gateway at most this often (at a
 * row-group boundary), so a call that ran out of budget resumes there.
 */
export const USAGE_ROLLUP_COLD_CHECKPOINT_INTERVAL_MS = 60_000;
/** A parked scan older than this is dropped and the day starts over (the gateway sweeps staging after 24 h). */
export const USAGE_ROLLUP_COLD_CHECKPOINT_MAX_AGE_MS = 23 * 3_600_000;
/** Rows per `cold/stage` chunk. */
export const USAGE_ROLLUP_COLD_STAGE_CHUNK_ROWS = 2_000;
/**
 * A day that should be folded and is not this long after it ended is an alert
 * (`day_unfolded_overdue`). Generous: the hot fold waits for the uplink.
 */
export const USAGE_ROLLUP_OVERDUE_HOURS = 8;
/** A day whose hot rows will be gone within this many hours and is not cold-verified yet is an alert. */
export const USAGE_ROLLUP_HOT_DEADLINE_WARN_HOURS = 6;

/** One row of `usage_dim_daily_rollup`: Shanghai day × org × account × member × key × vendor × model × sku × upstream × brand × status class × probe. */
export interface UsageDimRollupRow {
  readonly org_id: string;
  readonly account_id: string;
  /** The dashboard's key: `COALESCE(member_account_id, account_id)`, so a row without a member stores its account id. */
  readonly member_account_id: string;
  /**
   * The usage row's OWN member column: NULL when it had no member (gateway 0141). Part of the grain beside the
   * coalesced key, so a row without a member never merges into the account id it coalesces to.
   *
   * INVARIANT (both writers — the gateway's SQL fold and this folder — derive it from the same source value, the
   * schema has no CHECK for it): `COALESCE(raw_member_account_id, account_id) === member_account_id`, and it is NULL
   * exactly when the usage row had no member. A row that violates it is a fold bug, not an input to defend against.
   */
  readonly raw_member_account_id: string | null;
  readonly api_key_id: string;
  readonly vendor: string;
  readonly model: string;
  /** NULL sku stored as ''. */
  readonly sku_id: string;
  readonly upstream_id: string;
  /** NULL brand stored as ''. */
  readonly ingress_brand: string;
  /** NULL channel id stored as '' (gateway 0129). */
  readonly channel_id: string;
  /** NULL channel slug stored as '' (gateway 0129). */
  readonly channel_slug: string;
  /** `s` 200–399, `e` >= 400, `o` anything else. */
  readonly status_class: 's' | 'e' | 'o';
  readonly is_probe: 0 | 1;
  /** Part of the grain. NULL and '' are different rows, matching live `GROUP BY sku_name`. */
  readonly sku_name: string | null;
  readonly requests: number;
  /** 429 responses (a subset of `status_class = 'e'`). */
  readonly rate_limited_requests: number;
  /** 2xx–3xx responses with cache_read_tokens > 0. */
  readonly cache_hit_requests: number;
  /** Rows with no rated sibling whose `billing_outcome` is `rating_pending` (their money reads 0 until a verdict lands). */
  readonly pending_requests: number;
  readonly input_tokens: number;
  readonly output_tokens: number;
  readonly cache_read_tokens: number;
  readonly cache_write_tokens: number;
  readonly cache_eligible_input_tokens: number;
  /** The transition money: rated sibling when present, the frozen columns otherwise. */
  readonly cost_usd_micros: number;
  readonly latency_ms_sum: number;
  readonly latency_sample_count: number;
}

/**
 * One row of `usage_reader_daily_rollup`: the narrow day summary of the
 * short-window readers (routing preflight, probe-spend log, codex audience).
 * Every usage row of the day, retry attempts included (flagged), raw frozen
 * cost (no rated join).
 */
export interface UsageReaderRollupRow {
  readonly is_probe: 0 | 1;
  /** 1 for a `-aN` retry-attempt observability row. */
  readonly is_attempt: 0 | 1;
  readonly org_id: string;
  readonly account_id: string;
  readonly api_key_id: string;
  /** NULL sku stored as ''. */
  readonly sku_id: string;
  readonly sku_name: string;
  readonly protocol: string;
  readonly upstream_id: string;
  readonly channel_id: string;
  readonly channel_slug: string;
  readonly model: string;
  /**
   * `c` chargeable (gateway_sku_selector snapshot and cost > 0), `u` unpriced
   * (stamped, tokens burned, not chargeable), `p` pre-fix (no snapshot), `o`
   * other, `w` the day was folded from the warm archive, which does not carry
   * the cost-snapshot columns (probe-spend refuses such a day).
   */
  readonly cost_class: 'c' | 'u' | 'p' | 'o' | 'w';
  /** The procurement multipliers of a chargeable row; -1 outside class `c` or when the row has none. */
  readonly sku_cost_multiplier: number;
  readonly account_rate_multiplier: number;
  /**
   * Which columns were NULL in the usage rows (they store '' / -1 for them):
   * 1 sku_id, 2 sku_name, 4 channel_id, 8 channel_slug, 16 sku_cost_multiplier,
   * 32 account_rate_multiplier. Keeps NULL apart from '' like the live statements.
   */
  readonly null_mask: number;
  readonly requests: number;
  readonly input_tokens: number;
  readonly output_tokens: number;
  readonly logged_cost_usd_micros: number;
  /** ISO instant, microsecond precision as the row carries it: the newest `created_at` of the grain. */
  readonly last_created_at: string;
}

/** One row of `usage_org_hourly`. */
export interface UsageHourlyRollupRow {
  /** ISO instant of the hour start. */
  readonly hour_start: string;
  readonly org_id: string;
  readonly member_account_id: string;
  readonly api_key_id: string;
  readonly status_class: 's' | 'e' | 'o';
  readonly requests: number;
  readonly input_tokens: number;
  readonly output_tokens: number;
  readonly cache_read_tokens: number;
  readonly cache_write_tokens: number;
  readonly cost_usd_micros: number;
  /** ISO instant, microsecond precision as the cold row carries it. */
  readonly max_created_at: string;
}

/** One `usage_org_hourly_hours` fact of a cold-folded hour (all 24 hours of the day are sent). */
export interface UsageHourlyHourFact {
  readonly hour_start: string;
  /** Rows the fold counted (non-probe, non-retry). */
  readonly source_rows: number;
  /** Of those, rows with no rated sibling and `billing_outcome = 'rating_pending'`. */
  readonly pending_rows: number;
}

/** One row of `router_client_identity_daily`. */
export interface UsageIdentityRollupRow {
  readonly router_client_account_id: string;
  readonly router_client_device_id: string;
  readonly router_client_session_id: string;
  readonly request_count: number;
}

export type UsageRollupAlertKind =
  /** A complete day that should have been folded from the hot table is not. */
  | 'day_unfolded_overdue'
  /** A day's hot fold failed (the error is recorded on the state row). */
  | 'day_fold_failed'
  /** A day left (or is about to leave) the hot window with verdicts still pending and no cold verification. */
  | 'late_rating_beyond_hot_window'
  /** A day will leave the hot window within USAGE_ROLLUP_HOT_DEADLINE_WARN_HOURS without a cold verification. */
  | 'hot_deadline_near'
  /** The cold path could not rebuild a day (not published, rated side missing, row count mismatch). */
  | 'cold_unavailable'
  /** The cold recompute differed from the stored rollup and replaced it (informational, but an error line). */
  | 'cold_corrected'
  /** Observe tier: the cold recompute differs. Missing dimension rows may already have been inserted; the legacy tables were not changed. A human decides. */
  | 'cold_diverged';

export interface UsageRollupAlert {
  readonly kind: UsageRollupAlertKind;
  readonly family: UsageRollupFamily;
  readonly day: string;
  readonly detail: string;
}

/** Largest `lookbackDays` a fold or cold plan will honour. 400 covers 2026-04-19 from a 2027 release with room to spare. */
export const USAGE_ROLLUP_LOOKBACK_DAYS_MAX = 400;
/** Largest `maxDays` / `backfillPerFold` one hot fold call will honour. The call's own budget still stops it. */
export const USAGE_ROLLUP_FOLD_MAX_DAYS_CAP = 20;

/** A finite integer inside `[min, max]`, else `undefined` (the caller keeps its constant). */
export function usageRollupBoundedInt(value: unknown, min: number, max: number): number | undefined {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < min || value > max) return undefined;
  return value;
}

export interface UsageRollupFoldRequest {
  readonly budgetMs?: number;
  /**
   * Days folded this call, refolds included. Absent = `USAGE_ROLLUP_MAX_DAYS_PER_FOLD`.
   * The call still stops at `budgetMs`.
   */
  readonly maxDays?: number;
  /** History days queued this call. Absent = `USAGE_ROLLUP_BACKFILL_DAYS_PER_FOLD`. */
  readonly backfillPerFold?: number;
  /** How far back the queue looks. Absent = `USAGE_ROLLUP_COLD_BACKFILL_DAYS`. */
  readonly lookbackDays?: number;
  /**
   * Narrow-table backfill: derive at most this many folded days that lack a
   * narrow stamp (`usage_narrow_day_coverage`) from their own dimension rows.
   * Absent or 0 = the pass does not run and the response has no `narrow`.
   */
  readonly narrowDays?: number;
}

/** The share of one fold call the narrow-table backfill may use (it derives from summaries, not usage rows). */
export const USAGE_ROLLUP_NARROW_PHASE_MS = 20_000;

export type UsageRollupNarrowBlockedReason = 'not_folded' | 'old_fold_version' | 'diverged' | 'hot_unsettled' | 'no_reader_rows';

/** What a fold call's narrow-table backfill did. Present only when the request asked for it. */
export interface UsageRollupNarrowReport {
  /** Days completed this call and the families derived (`org`, `key`, `admin`, `ops`). */
  readonly derived: readonly { readonly day: string; readonly kinds: readonly string[] }[];
  /** Days that lack a stamp and could not be derived now, with the reason. */
  readonly blocked: readonly { readonly day: string; readonly reason: UsageRollupNarrowBlockedReason }[];
  /** Days whose derive or verify failed (rolled back, recorded as `day_fold_failed`). */
  readonly failed: readonly { readonly day: string; readonly error: string }[];
  /** Derivable days still waiting after this call. */
  readonly remaining: number;
}

export type UsageRollupUplinkGate = 'none' | 'settling' | 'open' | 'closed';

export interface UsageRollupFoldedDay {
  readonly day: string;
  /** Every usage row of the day (probes and retry attempts included). */
  readonly sourceRows: number;
  readonly pendingRows: number;
  readonly dimRows: number;
  readonly reason: 'new' | 'settle' | 'pending' | 'fingerprint' | 'version';
}

export interface UsageRollupFoldResponse {
  readonly ok: boolean;
  /** `disabled`: USAGE_ROLLUP_EAGER is off; `locked`: another fold holds the lock; `refused_cn`: CN gateway. */
  readonly skipped?: 'disabled' | 'locked' | 'refused_cn';
  readonly error?: string;
  readonly gate: UsageRollupUplinkGate | null;
  readonly folded: readonly UsageRollupFoldedDay[];
  /** Days re-checked and found equal. */
  readonly verifiedDays: readonly string[];
  readonly alerts: readonly UsageRollupAlert[];
  readonly budgetExhausted: boolean;
  readonly elapsedMs: number;
  /** Only when the request asked for the narrow-table backfill (`narrowDays` > 0). */
  readonly narrow?: UsageRollupNarrowReport;
}

export interface UsageRollupColdPlanDay {
  readonly day: string;
  readonly families: readonly UsageRollupFamily[];
}

/** A cold-verified day whose revisions the platform should compare with the cold layer's current ones (a late write publishes a new revision). */
export interface UsageRollupColdRevisionCheck {
  readonly day: string;
  readonly families: readonly UsageRollupFamily[];
  readonly usageRev: number;
  readonly usageToken: string;
  readonly ratedRev: number | null;
  readonly ratedToken: string | null;
}

export interface UsageRollupColdPlanResponse {
  readonly ok: boolean;
  readonly skipped?: 'disabled' | 'refused_cn';
  readonly error?: string;
  /**
   * Days no longer hot that still lack a cold verification, newest first. The
   * gateway leases the returned days for USAGE_ROLLUP_COLD_LEASE_MS: the same
   * day is not planned again until the lease ends or a commit / report lands.
   */
  readonly days: readonly UsageRollupColdPlanDay[];
  /** Total such days (the plan is capped by `limit`). */
  readonly totalDue: number;
  /** Verified days due a revision check (oldest check first), leased like `days`. */
  readonly revisionChecks?: readonly UsageRollupColdRevisionCheck[];
}

/** One `usage_org_hourly_units` row the cold fold stages next to the hourly family. */
export interface UsageHourlyUnitStageRow {
  readonly org_id: string;
  readonly hour_start: string;
  readonly member_account_id: string;
  readonly fallback_account_id: string;
  readonly api_key_id: string;
  readonly vendor: string;
  readonly model: string;
  readonly unit: string;
  readonly quantity: number;
  readonly requests: number;
}

/** What a stage chunk can carry: a family, or the reader summary that is committed together with `dim`. */
export type UsageRollupStageFamily = UsageRollupFamily | 'reader' | 'checkpoint' | 'units';

export interface UsageRollupColdStageRequest {
  readonly runId: string;
  readonly family: UsageRollupStageFamily;
  readonly day: string;
  readonly seq: number;
  readonly rows: readonly (UsageDimRollupRow | UsageReaderRollupRow | UsageHourlyRollupRow | UsageIdentityRollupRow | UsageHourlyUnitStageRow | UsageRollupCheckpointRow)[];
}

/** A row of a parked cold scan (family `checkpoint`): the meta row in chunk 0, folder entries in the others. */
export type UsageRollupCheckpointRow = Readonly<Record<string, unknown>>;

/** `GET cold/checkpoint?day=` (newest complete checkpoint: `meta`) and `?day=&runId=&seq=` (one chunk: `rows`). */
export interface UsageRollupColdCheckpointResponse {
  readonly ok: boolean;
  readonly error?: string;
  readonly found: boolean;
  readonly runId?: string;
  readonly meta?: UsageRollupCheckpointRow;
  readonly rows?: readonly UsageRollupCheckpointRow[];
}

export interface UsageRollupColdStageResponse {
  readonly ok: boolean;
  readonly error?: string;
  readonly staged: number;
}

/** What the cold fold learned about one family of one day, besides its rows. */
export interface UsageRollupColdFamilyFacts {
  readonly family: UsageRollupFamily;
  /** Rows of the family expected in staging (the commit refuses a different count). */
  readonly stagedRows: number;
  /** Every usage row of the day, probes and retry attempts included. */
  readonly sourceRows: number;
  /** Non-probe, non-retry rows with no rated sibling and `billing_outcome = 'rating_pending'`. */
  readonly pendingRows: number;
  /** `dim` only: rows of the reader summary staged under family `reader` (the commit refuses a different count). */
  readonly readerRows?: number;
  /** `hourly` only: one fact per hour of the day. */
  readonly hours?: readonly UsageHourlyHourFact[];
}

export interface UsageRollupColdRevisions {
  readonly usageRev: number;
  readonly usageToken: string;
  /** null for a day before the first rated day (no `usage.rated_events` exists to join). */
  readonly ratedRev: number | null;
  readonly ratedToken: string | null;
}

export interface UsageRollupColdCommitRequest {
  readonly runId: string;
  readonly day: string;
  readonly families: readonly UsageRollupColdFamilyFacts[];
  readonly revisions: UsageRollupColdRevisions;
}

/**
 * What the cold path reports about a day without committing it:
 *   `not_cleared`    the cold layer does not publish it (yet) or its rated side is not ready;
 *   `unavailable`    it does, and the rebuild failed or did not add up;
 *   `not_applicable` the published revision cannot provide the family at all (an
 *                    H0 warm-archive export has no router-client identity): terminal;
 *   `unchanged`      a revision check found the cold revisions equal to the ones
 *                    the verified day was computed from: only the check time moves;
 *   `stale`          a revision check found a newer revision (late write): the
 *                    day's cold verification is dropped so the plan lists it again.
 *   `in_progress`    the scan ran out of its call's budget with a parked
 *                    checkpoint: only the lease is released, so the next plan
 *                    hands the day out again at once (no back-off).
 */
export interface UsageRollupColdReportRequest {
  readonly day: string;
  readonly families: readonly UsageRollupFamily[];
  readonly status: 'not_cleared' | 'unavailable' | 'not_applicable' | 'unchanged' | 'stale' | 'in_progress';
  readonly detail: string;
}

/** `diverged`: the observe tier saw a difference between the cold recompute and the stored rollup and applied nothing. */
export type UsageRollupColdOutcome = 'equal' | 'replaced' | 'inserted' | 'diverged';

export interface UsageRollupColdCommitResponse {
  readonly ok: boolean;
  readonly error?: string;
  readonly day: string;
  readonly results: readonly {
    readonly family: UsageRollupFamily;
    readonly outcome: UsageRollupColdOutcome;
    /** Rows stored before and after (for `replaced`, the two differ or their content does). */
    readonly rowsBefore: number;
    readonly rowsAfter: number;
  }[];
  readonly alerts: readonly UsageRollupAlert[];
}

export interface UsageRollupColdRefoldRequest {
  readonly budgetMs?: number;
  readonly maxDays?: number;
  /** Dim-family cold plan depth. Absent = `USAGE_ROLLUP_COLD_BACKFILL_DAYS` (91). */
  readonly lookbackDays?: number;
}

export interface UsageRollupColdRefoldDay {
  readonly day: string;
  readonly status: 'committed' | 'unavailable' | 'failed' | 'skipped';
  readonly outcomes: Readonly<Partial<Record<UsageRollupFamily, UsageRollupColdOutcome>>>;
  readonly detail?: string;
}

export interface UsageRollupColdRefoldResponse {
  readonly ok: boolean;
  readonly skipped?: 'disabled' | 'no_cold_reader' | 'no_budget' | 'off';
  readonly error?: string;
  readonly days: readonly UsageRollupColdRefoldDay[];
  readonly totalDue: number;
  readonly alerts: readonly UsageRollupAlert[];
  readonly elapsedMs: number;
}
